import { ActionButton } from "@hopper-b2b/ui-core";
import { useI18nContext } from "@hopper-b2b/i18n";
import { IMonthBucket, ITripTerminus, TripCategory } from "@hopper-b2b/types";
import {
  DesktopPopupModal,
  IconComponent,
  IconName,
  MobilePopoverCard,
} from "@hopper-b2b/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import { useCallback, useMemo } from "react";
import styles from "./CalendarPicker.module.scss";
import {
  MonthAndDatePicker,
  MonthAndDatePickerType,
} from "./MonthAndDatePicker";
import { Suggestion } from "@b2bportal/air-shopping-api";

export interface CalendarTrackingProperties {
  origin: string;
  destination: string;
  tripCategory: TripCategory;
}

export interface ICalendarPickerProps {
  open?: boolean;
  departureDate: Date | null;
  returnDate: Date | null;
  setDepartureDate: (value: Date | null) => void;
  setReturnDate: (value: Date | null) => void;
  closePopup?: () => void;
  disabled: boolean;
  tripCategory: TripCategory;
  focusedMonthIndex?: number;
  onComplete?: ({
    origin,
    destination,
    deptDate,
    retDate,
  }: {
    origin?: Suggestion | ITripTerminus | null;
    destination?: Suggestion | ITripTerminus | null;
    deptDate?: Date | null;
    retDate?: Date | null;
  }) => void;
  isHistoryPushDisabled?: boolean;
  isMobile?: boolean;
  modal?: boolean;
  trackingProperties: CalendarTrackingProperties;
  prices?: string[];
  months?: IMonthBucket[];
  headerTitle: string;
  startDateLabel?: string;
  endDateLabel?: string;
  paperClassName?: string;
  preventSameDateSelection?: boolean;
}

export const CalendarPicker = ({
  open,
  closePopup,
  disabled,
  tripCategory,
  departureDate,
  returnDate,
  onComplete,
  isMobile = false,
  modal = false,
  setDepartureDate,
  setReturnDate,
  prices,
  months,
  startDateLabel,
  endDateLabel,
  headerTitle,
  paperClassName,
  preventSameDateSelection,
}: ICalendarPickerProps) => {
  const { t } = useI18nContext();

  const focusedMonthIndex = departureDate
    ? dayjs(departureDate).diff(dayjs(), "month")
    : 0;

  const calendarClassName = useMemo(
    () => (tripCategory === TripCategory.ONE_WAY ? "one-way" : "roundtrip"),
    [tripCategory]
  );

  const onDatesSelect = useCallback(() => {
    onComplete ? onComplete({}) : closePopup?.();
  }, [closePopup, onComplete]);

  const readyToSearch = useMemo(() => {
    return tripCategory === TripCategory.ONE_WAY
      ? !!departureDate
      : tripCategory === TripCategory.ROUND_TRIP
      ? !!returnDate && !!departureDate
      : false;
  }, [tripCategory, departureDate, returnDate]);

  return isMobile ? (
    <MobilePopoverCard
      className={styles["mobile-calendar-picker-root"]}
      fullScreen
      open={open}
      onClose={closePopup}
      paperClassName={paperClassName}
      topRightButton={
        <button className="close-mobile-calendar" onClick={closePopup}>
          <IconComponent name={IconName.Close} />
        </button>
      }
      bottomButton={
        readyToSearch ? (
          <ActionButton
            className="mobile-calendar-picker-search-button"
            onClick={onDatesSelect}
            message={t("searchControl.calendarPicker")}
            disabled={!readyToSearch}
          />
        ) : null
      }
    >
      <MonthAndDatePicker
        tripCategory={tripCategory}
        currency=""
        className={clsx(`b2b ${calendarClassName}`, {
          "search-ready": readyToSearch,
          modal,
        })}
        priceTags={prices}
        viewType={MonthAndDatePickerType.Column}
        focusedMonthIndex={focusedMonthIndex}
        setStartDate={setDepartureDate}
        setEndDate={setReturnDate}
        startDate={departureDate}
        endDate={returnDate}
        months={months}
        header={headerTitle}
        startDateLabel={startDateLabel}
        endDateLabel={endDateLabel}
        preventSameDateSelection={preventSameDateSelection}
      />
    </MobilePopoverCard>
  ) : (
    <DesktopPopupModal
      open={Boolean(open)}
      onClose={closePopup}
      className={clsx(
        styles["desktop-calendar-picker-popup-root"],
        "desktop-flight-calendar-picker-popup-root",
        "flights-module",
        calendarClassName
      )}
      contentClassName="desktop-calendar-picker-wrapper"
      invisibleBackdrop={false}
    >
      <MonthAndDatePicker
        tripCategory={tripCategory}
        currency=""
        className={clsx(`b2b ${calendarClassName}`, {
          "search-ready": readyToSearch,
          modal,
        })}
        priceTags={prices}
        viewType={MonthAndDatePickerType.Horizontal}
        focusedMonthIndex={focusedMonthIndex}
        setStartDate={setDepartureDate}
        setEndDate={setReturnDate}
        startDate={departureDate}
        endDate={returnDate}
        header={headerTitle}
        months={months}
        preventSameDateSelection={preventSameDateSelection}
      />
      <ActionButton
        onClick={onDatesSelect}
        disabled={disabled}
        className="select-dates-button"
        message={t("searchControl.calendarPicker")}
      />
    </DesktopPopupModal>
  );
};
